import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Shield, Rocket, Map, Crown, Lock, TrendingUp, Compass } from "lucide-react";
import { StatusChip } from "@/components/common";
import { useLang } from "@/i18n";

const MOTIFS = {
  bull: { Icon: TrendingUp, hue: "from-gold/30 via-[#1a1206] to-ink" },
  shield: { Icon: Shield, hue: "from-brgreen/30 via-[#06120a] to-ink" },
  pioneer: { Icon: Compass, hue: "from-brblue/50 via-[#050a18] to-ink" },
  map: { Icon: Map, hue: "from-neon/20 via-[#0a1206] to-ink" },
  capybara: { Icon: Crown, hue: "from-gold/25 via-[#120e04] to-ink" },
  vault: { Icon: Lock, hue: "from-gold/20 via-[#0d0b05] to-ink" },
  rocket: { Icon: Rocket, hue: "from-brgreen/30 via-[#06120a] to-ink" },
};

export function NftArt({ nft, className = "" }) {
  if (nft.image) {
    return <img src={nft.image} alt={nft.name} loading="lazy" className={`h-full w-full object-cover ${className}`} />;
  }
  const motif = MOTIFS[nft.motif] || MOTIFS.shield;
  return (
    <div className={`relative flex h-full w-full items-center justify-center bg-gradient-to-br ${motif.hue} ${className}`}>
      <div aria-hidden="true" className="absolute inset-0 opacity-[0.12]"
        style={{ backgroundImage: "repeating-linear-gradient(115deg, transparent 0 14px, rgba(212,175,55,.5) 14px 15px)" }} />
      <motif.Icon size={88} strokeWidth={1} className="text-gold drop-shadow-[0_0_24px_rgba(212,175,55,.45)]" aria-hidden="true" />
      <span className="absolute bottom-3 right-4 font-display text-4xl uppercase text-white/10">{nft.id}</span>
    </div>
  );
}

export function NftCard({ nft, index = 0 }) {
  const { t, lang } = useLang();
  const ref = useRef(null);
  const [tilt, setTilt] = useState({ rx: 0, ry: 0 });

  const onMove = (e) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    const px = (e.clientX - r.left) / r.width - 0.5;
    const py = (e.clientY - r.top) / r.height - 0.5;
    setTilt({ rx: -py * 10, ry: px * 12 });
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.6, delay: (index % 4) * 0.08, ease: [0.22, 1, 0.36, 1] }}
    >
      <Link
        to={`/nfts/${nft.slug}`}
        data-testid={`nft-card-${nft.slug}`}
        className="group block"
        style={{ perspective: "900px" }}
      >
        <div
          ref={ref}
          onMouseMove={onMove}
          onMouseLeave={() => setTilt({ rx: 0, ry: 0 })}
          className="card-surface overflow-hidden !rounded-2xl"
          style={{
            transform: `rotateX(${tilt.rx}deg) rotateY(${tilt.ry}deg)`,
            transition: "transform .18s ease-out, border-color .35s, box-shadow .35s",
            transformStyle: "preserve-3d",
          }}
        >
          <div className="relative aspect-square overflow-hidden">
            <NftArt nft={nft} className="transition-transform duration-500 group-hover:scale-105" />
            <div className="absolute inset-0 bg-gradient-to-t from-ink/90 via-transparent to-transparent" />
            <div className="absolute left-3 top-3">
              <StatusChip status={nft.status} label={t("common.comingSoon")} />
            </div>
            <span className="absolute right-3 top-3 font-mono text-[10px] uppercase tracking-widest text-goldlight/80">{nft.id}</span>
          </div>
          <div className="space-y-2 p-4">
            <h3 className="font-heading text-base font-bold text-paper transition-colors group-hover:text-goldlight">{nft.name}</h3>
            <div className="flex items-center justify-between">
              <span className="font-mono text-[11px] uppercase tracking-wider text-mist">{nft.rarity}</span>
              <span className="font-heading text-xs font-semibold uppercase tracking-widest text-neon opacity-0 transition-opacity group-hover:opacity-100">
                {t("nft.viewDetails")} →
              </span>
            </div>
          </div>
        </div>
      </Link>
    </motion.div>
  );
}
